import React, { useState, useRef, useEffect } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { InputTextarea } from "primereact/inputtextarea";
import { Divider } from "primereact/divider";
import { Checkbox } from "primereact/checkbox";
import { Toast } from "primereact/toast";
import axios from "axios";
import SignatureCanvas from "react-signature-canvas";
import BarcodeScannerComponent from "react-qr-barcode-scanner";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "../../../firebase/firebase";
import offlineDeliveryService from "../../../services/offlineDeliveryService";
import { DEV } from "../../../services/constants";
import styles from "./DeliveryStopDialog.module.scss";

const DeliveryStopDialog = ({ visible, onHide, stop, deliveryId, onStopUpdated, toast: parentToast }) => {
    const [activeTab, setActiveTab] = useState("scan");
    const [scannedPackages, setScannedPackages] = useState([]);
    const [photoDataUrl, setPhotoDataUrl] = useState(null);
    const [photoUrl, setPhotoUrl] = useState(null);
    const [signatureDataUrl, setSignatureDataUrl] = useState(null);
    const [signatureUrl, setSignatureUrl] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
    const [isUploadingSignature, setIsUploadingSignature] = useState(false);
    const [showIncompleteDialog, setShowIncompleteDialog] = useState(false);
    const [showSkipDialog, setShowSkipDialog] = useState(false);
    const [incompleteReason, setIncompleteReason] = useState("");
    const [skipReason, setSkipReason] = useState("");
    const [customIncompleteReason, setCustomIncompleteReason] = useState("");
    const [customSkipReason, setCustomSkipReason] = useState("");
    const [isMarkingIncomplete, setIsMarkingIncomplete] = useState(false);
    const [isSkipping, setIsSkipping] = useState(false);
    const [scannerActive, setScannerActive] = useState(false);

    const signatureRef = useRef(null);
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const toast = useRef(null);
    const fileInputRef = useRef(null);
    const [stream, setStream] = useState(null);
    const [cameraActive, setCameraActive] = useState(false);
    const lastScannedWaybill = useRef(null);
    const lastScanTime = useRef(0);

    // Helper function to show toast
    const showToast = (severity, summary, detail, life = 3000) => {
        const toastRef = toast.current || parentToast?.current;
        if (toastRef) {
            toastRef.show({ severity, summary, detail, life });
        }
    };

    // Reset state when dialog opens
    useEffect(() => {
        if (visible && stop) {
            setActiveTab("scan");
            setScannedPackages([]);
            setPhotoDataUrl(null);
            setPhotoUrl(null);
            setSignatureDataUrl(null);
            setSignatureUrl(null);
            setIncompleteReason("");
            setSkipReason("");
            setCustomIncompleteReason("");
            setCustomSkipReason("");
            setScannerActive(false);
        }
    }, [visible, stop]);

    // Cleanup camera stream
    useEffect(() => {
        return () => {
            if (stream) {
                stream.getTracks().forEach((track) => track.stop());
            }
        };
    }, [stream]);

    if (!stop) return null;

    const allPackages = stop.packages || [];
    const totalPackages = allPackages.length;
    const allScanned = scannedPackages.length === totalPackages && totalPackages > 0;
    const hasScannedPackages = scannedPackages.length > 0;
    const hasPartialScan = hasScannedPackages && !allScanned;

    // Handle barcode scan
    const handleScan = (err, result) => {
        if (result) {
            const scannedWaybill = result.text.trim();

            if (!scannedWaybill || scannedWaybill.length === 0) {
                console.log("Empty scan result ignored");
                return;
            }

            const now = Date.now();
            const SCAN_COOLDOWN = 2000;

            if (lastScannedWaybill.current === scannedWaybill && now - lastScanTime.current < SCAN_COOLDOWN) {
                console.log(`Duplicate scan ignored: ${scannedWaybill} (cooldown active)`);
                return;
            }

            lastScannedWaybill.current = scannedWaybill;
            lastScanTime.current = now;

            console.log("Processing scan:", scannedWaybill);

            const foundPackage = allPackages.find((pkg) => pkg.Waybill === scannedWaybill);

            if (foundPackage) {
                console.log("Package found:", foundPackage);

                if (scannedPackages.includes(foundPackage._id)) {
                    console.log("Package already scanned:", foundPackage._id);

                    showToast("info", "Already Scanned", `Package ${scannedWaybill} is already in the list`, 2000);

                    if (navigator.vibrate) {
                        navigator.vibrate([100, 100]);
                    }
                } else {
                    setScannedPackages((prev) => {
                        if (prev.includes(foundPackage._id)) {
                            console.log("Race condition prevented for:", foundPackage._id);
                            return prev;
                        }

                        console.log("Adding package to scanned list:", foundPackage._id);
                        return [...prev, foundPackage._id];
                    });

                    showToast("success", "Package Scanned", `${scannedWaybill} (${scannedPackages.length + 1}/${allPackages.length})`, 2000);

                    if (navigator.vibrate) {
                        navigator.vibrate(200);
                    }
                }
            } else {
                console.log("Package not found in delivery:", scannedWaybill);

                showToast("error", "Package Not Found", `Waybill ${scannedWaybill} is not in this delivery`, 3000);

                if (navigator.vibrate) {
                    navigator.vibrate([100, 50, 100, 50, 100]);
                }
            }
        }

        if (err) {
            if (err.name !== "NotFoundException") {
                console.error("Scanner error:", err);

                if (err.name === "NotAllowedError") {
                    showToast("error", "Camera Access Denied", "Please allow camera access to scan packages", 4000);
                }
            }
        }
    };

    // Toggle package scan manually
    const togglePackageScan = (packageId) => {
        setScannedPackages((prev) => (prev.includes(packageId) ? prev.filter((id) => id !== packageId) : [...prev, packageId]));
    };

    // Open incomplete dialog
    const openIncompleteDialog = () => {
        setIncompleteReason("");
        setCustomIncompleteReason("");
        setShowIncompleteDialog(true);
    };

    // Open skip dialog
    const openSkipDialog = () => {
        setSkipReason("");
        setCustomSkipReason("");
        setShowSkipDialog(true);
    };

    // Start camera
    const startCamera = async () => {
        try {
            if (stream) {
                stream.getTracks().forEach((track) => track.stop());
            }

            console.log("Requesting camera access...");

            const constraints = {
                video: {
                    facingMode: { ideal: "environment" },
                    width: { ideal: 1920, max: 1920 },
                    height: { ideal: 1080, max: 1080 },
                },
                audio: false,
            };

            const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);

            console.log("Camera access granted");

            if (videoRef.current) {
                videoRef.current.srcObject = mediaStream;

                videoRef.current.onloadedmetadata = () => {
                    console.log("Video metadata loaded");
                    videoRef.current
                        .play()
                        .then(() => {
                            console.log("Video playing");
                            setStream(mediaStream);
                            setCameraActive(true);
                        })
                        .catch((err) => {
                            console.error("Error playing video:", err);
                            showToast("error", "Playback Error", "Failed to start video playback");
                        });
                };
            }
        } catch (error) {
            console.error("Camera error:", error);
            let errorMessage = "Unable to access camera. ";

            if (error.name === "NotAllowedError") {
                errorMessage += "Please allow camera permissions.";
            } else if (error.name === "NotFoundError") {
                errorMessage += "No camera device found.";
            } else if (error.name === "NotReadableError") {
                errorMessage += "Camera is already in use.";
            } else {
                errorMessage += error.message;
            }

            showToast("error", "Camera Error", errorMessage);
        }
    };

    // Capture photo from camera
    const capturePhoto = () => {
        if (videoRef.current && canvasRef.current) {
            const video = videoRef.current;
            const canvas = canvasRef.current;

            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;

            const ctx = canvas.getContext("2d");
            ctx.drawImage(video, 0, 0);

            const dataUrl = canvas.toDataURL("image/jpeg", 0.8);
            setPhotoDataUrl(dataUrl);
            stopCamera();

            showToast("success", "Photo Captured", "Delivery photo captured successfully", 2000);
        }
    };

    // Stop camera
    const stopCamera = () => {
        if (stream) {
            stream.getTracks().forEach((track) => track.stop());
            setStream(null);
            setCameraActive(false);
        }
    };

    // Upload photo from file
    const handleFileUpload = (event) => {
        const file = event.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setPhotoDataUrl(reader.result);
                showToast("success", "Photo Uploaded", "Delivery photo uploaded successfully", 2000);
            };
            reader.readAsDataURL(file);
        }
    };

    // Upload photo to Firebase (with offline check)
    const uploadPhotoToFirebase = async () => {
        if (!photoDataUrl) return null;

        // Check if online
        if (!navigator.onLine) {
            console.log("📴 Offline - Will upload photo later");
            return null; // Don't upload now, will be handled during sync
        }

        setIsUploadingPhoto(true);
        try {
            const photoBlob = await fetch(photoDataUrl).then((r) => r.blob());
            const timestamp = Date.now();
            const fileName = `deliveries/${deliveryId}/stops/${stop._id}/photo_${timestamp}.jpg`;
            const storageRef = ref(storage, fileName);

            await uploadBytes(storageRef, photoBlob);
            const downloadURL = await getDownloadURL(storageRef);

            setPhotoUrl(downloadURL);
            showToast("success", "Photo Uploaded", "Photo uploaded to storage", 2000);
            return downloadURL;
        } catch (error) {
            console.error("Photo upload error:", error);

            // If network error, don't show error - will be saved offline
            if (error.message === "Network Error" || !navigator.onLine) {
                console.log("📴 Network error during upload - will save offline");
                return null;
            }

            showToast("error", "Upload Failed", "Failed to upload photo");
            return null;
        } finally {
            setIsUploadingPhoto(false);
        }
    };

    // Clear signature
    const clearSignature = () => {
        if (signatureRef.current) {
            signatureRef.current.clear();
            setSignatureDataUrl(null);
        }
    };

    // Save signature
    const saveSignature = () => {
        if (signatureRef.current && !signatureRef.current.isEmpty()) {
            const dataUrl = signatureRef.current.toDataURL();
            setSignatureDataUrl(dataUrl);
            showToast("success", "Signature Saved", "Customer signature captured", 2000);
        }
    };

    // Upload signature to Firebase (with offline check)
    const uploadSignatureToFirebase = async () => {
        if (!signatureDataUrl) return null;

        // Check if online
        if (!navigator.onLine) {
            console.log("📴 Offline - Will upload signature later");
            return null;
        }

        setIsUploadingSignature(true);
        try {
            const signatureBlob = await fetch(signatureDataUrl).then((r) => r.blob());
            const timestamp = Date.now();
            const fileName = `deliveries/${deliveryId}/stops/${stop._id}/signature_${timestamp}.png`;
            const storageRef = ref(storage, fileName);

            await uploadBytes(storageRef, signatureBlob);
            const downloadURL = await getDownloadURL(storageRef);

            setSignatureUrl(downloadURL);
            showToast("success", "Signature Uploaded", "Signature uploaded to storage", 2000);
            return downloadURL;
        } catch (error) {
            console.error("Signature upload error:", error);

            if (error.message === "Network Error" || !navigator.onLine) {
                console.log("📴 Network error during upload - will save offline");
                return null;
            }

            showToast("error", "Upload Failed", "Failed to upload signature");
            return null;
        } finally {
            setIsUploadingSignature(false);
        }
    };

    // Submit delivery proof (WITH OFFLINE SUPPORT)
    const handleSubmitDelivery = async () => {
        // Validation
        if (scannedPackages.length === 0) {
            showToast("warn", "Packages Required", "Please scan at least one package");
            return;
        }

        if (!photoDataUrl) {
            showToast("warn", "Photo Required", "Please capture a delivery photo");
            return;
        }

        if (!signatureDataUrl) {
            showToast("warn", "Signature Required", "Please capture customer signature");
            return;
        }

        if (hasPartialScan) {
            showToast("warn", "Partial Delivery", "Not all packages scanned. Please use 'Mark Remaining as Incomplete' button.");
            return;
        }

        setIsSubmitting(true);

        try {
            // Check if online
            if (!navigator.onLine) {
                console.log("📴 Offline - Saving delivery proof locally");

                const offlineData = {
                    deliveryId,
                    stopId: stop._id,
                    stopNumber: stop.stopNumber,
                    clientName: stop.clientName,
                    photoDataUrl,
                    signatureDataUrl,
                    scannedPackageIds: scannedPackages,
                    timestamp: new Date().toISOString(),
                    type: "complete",
                };

                offlineDeliveryService.saveOfflineDelivery(offlineData);

                showToast("success", "Saved Offline", "Delivery proof saved. Will sync when online.", 5000);

                if (onStopUpdated) {
                    onStopUpdated({ offline: true, stopId: stop._id });
                }

                onHide();
                setIsSubmitting(false);
                return;
            }

            // ONLINE - Try to upload files
            let uploadedPhotoUrl = photoUrl;
            let uploadedSignatureUrl = signatureUrl;

            // Only upload if not already uploaded
            if (!uploadedPhotoUrl) {
                uploadedPhotoUrl = await uploadPhotoToFirebase();
            }

            if (!uploadedSignatureUrl) {
                uploadedSignatureUrl = await uploadSignatureToFirebase();
            }

            // If uploads failed due to network, save offline
            if (!uploadedPhotoUrl || !uploadedSignatureUrl) {
                console.log("📴 Upload failed - Saving offline");

                const offlineData = {
                    deliveryId,
                    stopId: stop._id,
                    stopNumber: stop.stopNumber,
                    clientName: stop.clientName,
                    photoDataUrl,
                    signatureDataUrl,
                    scannedPackageIds: scannedPackages,
                    timestamp: new Date().toISOString(),
                    type: "complete",
                };

                offlineDeliveryService.saveOfflineDelivery(offlineData);

                showToast("warning", "Saved Offline", "Network issue. Saved offline for later sync.", 5000);

                if (onStopUpdated) {
                    onStopUpdated({ offline: true, stopId: stop._id });
                }

                onHide();
                setIsSubmitting(false);
                return;
            }

            // Submit to API
            const response = await axios.post(
                `${DEV}/delivery/${deliveryId}/stop/${stop._id}/proof`,
                {
                    photoUrl: uploadedPhotoUrl,
                    signatureUrl: uploadedSignatureUrl,
                    scannedPackageIds: scannedPackages,
                    timestamp: new Date().toISOString(),
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${localStorage.getItem("token")}`,
                    },
                },
            );

            if (response.data.success) {
                showToast("success", "Delivery Complete", response.data.message);

                if (onStopUpdated) {
                    console.log("Updating stop with API response data:", response.data.data);
                    onStopUpdated(response.data.data);
                }

                onHide();
            }
        } catch (error) {
            console.error("Submit delivery error:", error);

            // If network error, save offline
            if (error.message === "Network Error" || !navigator.onLine) {
                console.log("📴 Network error - Saving offline");

                const offlineData = {
                    deliveryId,
                    stopId: stop._id,
                    stopNumber: stop.stopNumber,
                    clientName: stop.clientName,
                    photoDataUrl,
                    signatureDataUrl,
                    scannedPackageIds: scannedPackages,
                    timestamp: new Date().toISOString(),
                    type: "complete",
                };

                offlineDeliveryService.saveOfflineDelivery(offlineData);

                showToast("warning", "Saved Offline", "Network error. Delivery saved offline for later sync.", 5000);

                if (onStopUpdated) {
                    onStopUpdated({ offline: true, stopId: stop._id });
                }

                onHide();
            } else {
                const errorMessage = error.response?.data?.message || "Failed to submit delivery proof";
                showToast("error", "Submission Failed", errorMessage);
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    // Mark stop as incomplete (WITH OFFLINE SUPPORT)
    const handleMarkIncomplete = async () => {
        const finalReason = incompleteReason === "Other" ? customIncompleteReason : incompleteReason;

        if (!finalReason.trim()) {
            showToast("warn", "Reason Required", "Please provide a reason for marking incomplete");
            return;
        }

        setIsMarkingIncomplete(true);

        try {
            // Check if online
            if (!navigator.onLine) {
                console.log("📴 Offline - Saving incomplete status locally");

                const offlineData = {
                    deliveryId,
                    stopId: stop._id,
                    stopNumber: stop.stopNumber,
                    clientName: stop.clientName,
                    reason: finalReason,
                    photoDataUrl: photoDataUrl || null,
                    signatureDataUrl: signatureDataUrl || null,
                    scannedPackageIds: scannedPackages,
                    timestamp: new Date().toISOString(),
                    type: "incomplete",
                };

                offlineDeliveryService.saveOfflineDelivery(offlineData);

                showToast("warning", "Saved Offline", "Incomplete status saved. Will sync when online.", 5000);

                if (onStopUpdated) {
                    onStopUpdated({ offline: true, stopId: stop._id });
                }

                setShowIncompleteDialog(false);
                onHide();
                setIsMarkingIncomplete(false);
                return;
            }

            // ONLINE - Try to upload files if present
            let uploadedPhotoUrl = photoUrl;
            let uploadedSignatureUrl = signatureUrl;

            if (photoDataUrl && !uploadedPhotoUrl) {
                uploadedPhotoUrl = await uploadPhotoToFirebase();
            }

            if (signatureDataUrl && !uploadedSignatureUrl) {
                uploadedSignatureUrl = await uploadSignatureToFirebase();
            }

            // If we have partial data and uploads failed, save offline
            if ((photoDataUrl && !uploadedPhotoUrl) || (signatureDataUrl && !uploadedSignatureUrl)) {
                console.log("📴 Upload failed - Saving offline");

                const offlineData = {
                    deliveryId,
                    stopId: stop._id,
                    stopNumber: stop.stopNumber,
                    clientName: stop.clientName,
                    reason: finalReason,
                    photoDataUrl: photoDataUrl || null,
                    signatureDataUrl: signatureDataUrl || null,
                    scannedPackageIds: scannedPackages,
                    timestamp: new Date().toISOString(),
                    type: "incomplete",
                };

                offlineDeliveryService.saveOfflineDelivery(offlineData);

                showToast("warning", "Saved Offline", "Network issue. Saved offline for later sync.", 5000);

                if (onStopUpdated) {
                    onStopUpdated({ offline: true, stopId: stop._id });
                }

                setShowIncompleteDialog(false);
                onHide();
                setIsMarkingIncomplete(false);
                return;
            }

            // Submit to API
            const response = await axios.post(
                `${DEV}/delivery/${deliveryId}/stop/${stop._id}/mark-incomplete`,
                {
                    reason: finalReason,
                    photoUrl: uploadedPhotoUrl,
                    signatureUrl: uploadedSignatureUrl,
                    scannedPackageIds: scannedPackages,
                    timestamp: new Date().toISOString(),
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${localStorage.getItem("token")}`,
                    },
                },
            );

            if (response.status === 200 && response.data.success) {
                showToast("warn", "Stop Marked Incomplete", response.data.message);

                if (onStopUpdated) {
                    onStopUpdated(response.data.data);
                }

                setShowIncompleteDialog(false);
                onHide();
            }
        } catch (error) {
            console.error("Mark incomplete error:", error);

            // If network error, save offline
            if (error.message === "Network Error" || !navigator.onLine) {
                console.log("📴 Network error - Saving offline");

                const offlineData = {
                    deliveryId,
                    stopId: stop._id,
                    stopNumber: stop.stopNumber,
                    clientName: stop.clientName,
                    reason: finalReason,
                    photoDataUrl: photoDataUrl || null,
                    signatureDataUrl: signatureDataUrl || null,
                    scannedPackageIds: scannedPackages,
                    timestamp: new Date().toISOString(),
                    type: "incomplete",
                };

                offlineDeliveryService.saveOfflineDelivery(offlineData);

                showToast("warning", "Saved Offline", "Network error. Status saved offline for later sync.", 5000);

                if (onStopUpdated) {
                    onStopUpdated({ offline: true, stopId: stop._id });
                }

                setShowIncompleteDialog(false);
                onHide();
            } else {
                showToast("error", "Update Failed", "Failed to mark stop as incomplete");
            }
        } finally {
            setIsMarkingIncomplete(false);
        }
    };

    // Skip stop (WITH OFFLINE SUPPORT)
    const handleSkipStop = async () => {
        const finalReason = skipReason === "Other" ? customSkipReason : skipReason;

        if (!finalReason.trim()) {
            showToast("warn", "Reason Required", "Please provide a reason for skipping this stop");
            return;
        }

        setIsSkipping(true);

        try {
            // Check if online
            if (!navigator.onLine) {
                console.log("📴 Offline - Saving skip status locally");

                const offlineData = {
                    deliveryId,
                    stopId: stop._id,
                    stopNumber: stop.stopNumber,
                    clientName: stop.clientName,
                    reason: finalReason,
                    timestamp: new Date().toISOString(),
                    type: "skipped",
                };

                offlineDeliveryService.saveOfflineDelivery(offlineData);

                showToast("info", "Saved Offline", "Stop skipped. Will sync when online.", 5000);

                if (onStopUpdated) {
                    onStopUpdated({ offline: true, stopId: stop._id });
                }

                setShowSkipDialog(false);
                onHide();
                setIsSkipping(false);
                return;
            }

            // Submit to API
            const response = await axios.post(
                `${DEV}/delivery/${deliveryId}/stop/${stop._id}/mark-skipped`,
                {
                    reason: finalReason,
                    timestamp: new Date().toISOString(),
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${localStorage.getItem("token")}`,
                    },
                },
            );

            if (response.status === 200 && response.data.success) {
                showToast("info", "Stop Skipped", response.data.message);

                if (onStopUpdated) {
                    onStopUpdated(response.data.data);
                }

                setShowSkipDialog(false);
                onHide();
            }
        } catch (error) {
            console.error("Skip stop error:", error);

            // If network error, save offline
            if (error.message === "Network Error" || !navigator.onLine) {
                console.log("📴 Network error - Saving offline");

                const offlineData = {
                    deliveryId,
                    stopId: stop._id,
                    stopNumber: stop.stopNumber,
                    clientName: stop.clientName,
                    reason: finalReason,
                    timestamp: new Date().toISOString(),
                    type: "skipped",
                };

                offlineDeliveryService.saveOfflineDelivery(offlineData);

                showToast("info", "Saved Offline", "Stop skipped offline. Will sync when online.", 5000);

                if (onStopUpdated) {
                    onStopUpdated({ offline: true, stopId: stop._id });
                }

                setShowSkipDialog(false);
                onHide();
            } else {
                showToast("error", "Skip Failed", "Failed to skip stop");
            }
        } finally {
            setIsSkipping(false);
        }
    };

    // Progress calculation
    const getProgress = () => {
        let progress = 0;
        if (scannedPackages.length > 0) progress += 33;
        if (photoDataUrl) progress += 33;
        if (signatureDataUrl) progress += 34;
        return progress;
    };

    // Navigate back from tabs
    const handleBackNavigation = () => {
        if (activeTab === "signature") {
            setActiveTab("photo");
        } else if (activeTab === "photo") {
            setActiveTab("scan");
        } else if (activeTab === "complete") {
            setActiveTab("signature");
        }
    };

    return (
        <>
            <Toast ref={toast} />

            <Dialog
                visible={visible}
                onHide={onHide}
                header={
                    <div className={styles.dialogHeader}>
                        <div>
                            <h3>{stop.clientName}</h3>
                            <p>{stop.clientId?.email || "No email provided"}</p>
                        </div>
                        <div className={styles.stopBadge}>Stop #{stop.stopNumber}</div>
                    </div>
                }
                className={styles.deliveryDialog}
                draggable={false}
                dismissableMask
                blockScroll
            >
                <div className={styles.dialogContent}>
                    {!navigator.onLine && (
                        <div className={styles.offlineBanner}>
                            <i className="pi pi-wifi" style={{ textDecoration: "line-through" }} />
                            <span>Offline Mode - Data will be saved locally and synced when online</span>
                        </div>
                    )}

                    {activeTab !== "scan" && <Button icon="pi pi-arrow-left" label="Back" text className={styles.backButton} onClick={handleBackNavigation} />}

                    <div className={styles.progressSection}>
                        <div className={styles.progressBar}>
                            <div className={styles.progressFill} style={{ width: `${getProgress()}%` }} />
                        </div>
                        <p className={styles.progressText}>{getProgress()}% Complete</p>
                    </div>

                    <div className={styles.tabs}>
                        <button className={`${styles.tab} ${activeTab === "scan" ? styles.active : ""} ${scannedPackages.length > 0 ? styles.completed : ""}`} onClick={() => setActiveTab("scan")}>
                            <i className="pi pi-qrcode" />
                            <span>Scan</span>
                            {scannedPackages.length > 0 && <i className="pi pi-check-circle" />}
                        </button>
                        <button className={`${styles.tab} ${activeTab === "photo" ? styles.active : ""} ${photoDataUrl ? styles.completed : ""}`} onClick={() => setActiveTab("photo")}>
                            <i className="pi pi-camera" />
                            <span>Photo</span>
                            {photoDataUrl && <i className="pi pi-check-circle" />}
                        </button>
                        <button className={`${styles.tab} ${activeTab === "signature" ? styles.active : ""} ${signatureDataUrl ? styles.completed : ""}`} onClick={() => setActiveTab("signature")}>
                            <i className="pi pi-pencil" />
                            <span>Signature</span>
                            {signatureDataUrl && <i className="pi pi-check-circle" />}
                        </button>
                        <button className={`${styles.tab} ${activeTab === "complete" ? styles.active : ""}`} onClick={() => setActiveTab("complete")} disabled={!hasScannedPackages || !photoDataUrl || !signatureDataUrl}>
                            <i className="pi pi-check" />
                            <span>Complete</span>
                        </button>
                    </div>

                    <Divider />

                    <div className={styles.tabContent}>
                        {/* SCAN PACKAGES TAB */}
                        {activeTab === "scan" && (
                            <div className={styles.scanSection}>
                                <div className={styles.sectionHeader}>
                                    <h4>
                                        <i className="pi pi-qrcode" />
                                        Scan Packages
                                    </h4>
                                    <p>Scan barcodes to check off packages</p>
                                </div>

                                {scannerActive ? (
                                    <div className={styles.scannerContainer}>
                                        <BarcodeScannerComponent width="100%" height={300} onUpdate={handleScan} />
                                        <div className={styles.scannerOverlay}>
                                            <div className={styles.scanFrame} />
                                            <p className={styles.scanInstruction}>Align barcode within frame</p>
                                        </div>
                                        <Button label="Stop Scanner" icon="pi pi-times" onClick={() => setScannerActive(false)} severity="danger" className={styles.stopScanButton} />
                                    </div>
                                ) : (
                                    <div className={styles.scannerPlaceholder}>
                                        <i className="pi pi-qrcode" style={{ fontSize: "4rem", color: "#cbd5e1" }} />
                                        <Button label="Start Scanner" icon="pi pi-camera" onClick={() => setScannerActive(true)} size="large" className={styles.startScanButton} />
                                        <p className={styles.scanHint}>Or manually check packages below</p>
                                    </div>
                                )}

                                <Divider />

                                <div className={styles.packageList}>
                                    <h5 className={styles.packageListTitle}>
                                        Packages ({scannedPackages.length}/{totalPackages})
                                    </h5>
                                    {allPackages.map((pkg) => (
                                        <div key={pkg._id} className={`${styles.packageItem} ${scannedPackages.includes(pkg._id) ? styles.scanned : ""}`} onClick={() => togglePackageScan(pkg._id)}>
                                            <Checkbox checked={scannedPackages.includes(pkg._id)} onChange={() => togglePackageScan(pkg._id)} />
                                            <div className={styles.packageInfo}>
                                                <strong>{pkg.Waybill || "No Waybill"}</strong>
                                                <p>{pkg.description || "No description"}</p>
                                                {pkg.trackingNumber && <span className={styles.tracking}>{pkg.trackingNumber}</span>}
                                            </div>
                                            {scannedPackages.includes(pkg._id) && <i className="pi pi-check-circle" style={{ color: "#10b981", fontSize: "1.5rem" }} />}
                                        </div>
                                    ))}
                                </div>

                                <div className={styles.scanSummary}>
                                    {allScanned ? (
                                        <>
                                            <div className={styles.allScannedBadge}>
                                                <i className="pi pi-check-circle" />
                                                All packages scanned!
                                            </div>
                                            <Button label="Next: Take Photo" icon="pi pi-arrow-right" onClick={() => setActiveTab("photo")} className={styles.nextButton} />
                                        </>
                                    ) : hasScannedPackages ? (
                                        <>
                                            <p className={styles.scanProgress}>
                                                Scanned: <strong>{scannedPackages.length}</strong> / {totalPackages} packages
                                            </p>
                                            <Button label="Continue with Partial Scan" icon="pi pi-arrow-right" iconPos="right" onClick={() => setActiveTab("photo")} severity="warning" className={styles.partialContinueButton} />
                                            <Button label="Skip This Stop" icon="pi pi-step-forward" onClick={openSkipDialog} outlined severity="secondary" className={styles.skipButton} />
                                        </>
                                    ) : (
                                        <>
                                            <p className={styles.scanProgress}>
                                                Scanned: <strong>{scannedPackages.length}</strong> / {totalPackages} packages
                                            </p>
                                            <div className={styles.actionButtonsGroup}>
                                                <Button label="Skip This Stop" icon="pi pi-step-forward" onClick={openSkipDialog} outlined severity="secondary" className={styles.skipButton} />
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* PHOTO TAB */}
                        {activeTab === "photo" && (
                            <div className={styles.photoSection}>
                                <div className={styles.sectionHeader}>
                                    <h4>
                                        <i className="pi pi-camera" />
                                        Delivery Photo
                                    </h4>
                                    <p>Capture proof of delivery</p>
                                </div>

                                {!photoDataUrl ? (
                                    <div className={styles.cameraContainer}>
                                        <video ref={videoRef} autoPlay playsInline muted className={`${styles.video} ${cameraActive ? styles.videoActive : styles.videoHidden}`} />
                                        <canvas ref={canvasRef} className={styles.hiddenCanvas} />

                                        {!cameraActive ? (
                                            <div className={styles.photoOptions}>
                                                <Button label="Open Camera" icon="pi pi-camera" onClick={startCamera} className={styles.cameraButton} size="large" />
                                                <Button label="Upload Photo" icon="pi pi-upload" onClick={() => fileInputRef.current?.click()} outlined className={styles.uploadButton} size="large" />
                                                <input ref={fileInputRef} type="file" accept="image/*" capture="environment" style={{ display: "none" }} onChange={handleFileUpload} />
                                            </div>
                                        ) : (
                                            <div className={styles.cameraControls}>
                                                <Button icon="pi pi-times" rounded severity="danger" onClick={stopCamera} size="large" tooltip="Cancel" />
                                                <Button icon="pi pi-circle" rounded severity="success" onClick={capturePhoto} className={styles.captureButton} size="large" tooltip="Capture" />
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className={styles.photoPreview}>
                                        <img src={photoDataUrl} alt="Delivery" className={styles.previewImage} />
                                        <div className={styles.previewActions}>
                                            <Button
                                                label="Retake Photo"
                                                icon="pi pi-refresh"
                                                onClick={() => {
                                                    setPhotoDataUrl(null);
                                                    setPhotoUrl(null);
                                                }}
                                                outlined
                                                severity="warning"
                                                className={styles.actionButton}
                                            />
                                            {navigator.onLine ? (
                                                photoUrl ? (
                                                    <Button label="Next: Get Signature" icon="pi pi-arrow-right" iconPos="right" onClick={() => setActiveTab("signature")} className={styles.nextButton} />
                                                ) : (
                                                    <Button
                                                        label={isUploadingPhoto ? "Uploading..." : "Upload & Continue"}
                                                        icon={isUploadingPhoto ? "pi pi-spin pi-spinner" : "pi pi-arrow-right"}
                                                        iconPos="right"
                                                        onClick={async () => {
                                                            const url = await uploadPhotoToFirebase();
                                                            setActiveTab("signature");
                                                        }}
                                                        disabled={isUploadingPhoto}
                                                        className={styles.nextButton}
                                                    />
                                                )
                                            ) : (
                                                <Button label="Continue (Offline)" icon="pi pi-arrow-right" iconPos="right" onClick={() => setActiveTab("signature")} className={styles.nextButton} />
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* SIGNATURE TAB */}
                        {activeTab === "signature" && (
                            <div className={styles.signatureSection}>
                                <div className={styles.sectionHeader}>
                                    <h4>
                                        <i className="pi pi-pencil" />
                                        Customer Signature
                                    </h4>
                                    <p>Get customer to sign below</p>
                                </div>

                                {!signatureDataUrl ? (
                                    <>
                                        <div className={styles.signaturePad}>
                                            <SignatureCanvas ref={signatureRef} canvasProps={{ className: styles.signatureCanvas }} backgroundColor="#f8fafc" />
                                            <div className={styles.signatureLine}>
                                                <span>Customer Signature</span>
                                            </div>
                                        </div>
                                        <div className={styles.signatureControls}>
                                            <Button label="Clear" icon="pi pi-times" onClick={clearSignature} outlined severity="danger" />
                                            <Button label="Save Signature" icon="pi pi-check" onClick={saveSignature} className={styles.saveButton} />
                                        </div>
                                    </>
                                ) : (
                                    <div className={styles.signaturePreview}>
                                        <img src={signatureDataUrl} alt="Signature" className={styles.previewImage} />
                                        <div className={styles.previewActions}>
                                            <Button
                                                label="Redo Signature"
                                                icon="pi pi-refresh"
                                                onClick={() => {
                                                    setSignatureDataUrl(null);
                                                    setSignatureUrl(null);
                                                    clearSignature();
                                                }}
                                                outlined
                                                severity="warning"
                                                className={styles.actionButton}
                                            />
                                            {navigator.onLine ? (
                                                signatureUrl ? (
                                                    <Button label="Review & Complete" icon="pi pi-arrow-right" iconPos="right" onClick={() => setActiveTab("complete")} className={styles.nextButton} />
                                                ) : (
                                                    <Button
                                                        label={isUploadingSignature ? "Uploading..." : "Upload & Continue"}
                                                        icon={isUploadingSignature ? "pi pi-spin pi-spinner" : "pi pi-arrow-right"}
                                                        iconPos="right"
                                                        onClick={async () => {
                                                            const url = await uploadSignatureToFirebase();
                                                            setActiveTab("complete");
                                                        }}
                                                        disabled={isUploadingSignature}
                                                        className={styles.nextButton}
                                                    />
                                                )
                                            ) : (
                                                <Button label="Continue (Offline)" icon="pi pi-arrow-right" iconPos="right" onClick={() => setActiveTab("complete")} className={styles.nextButton} />
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* COMPLETE TAB */}
                        {activeTab === "complete" && (
                            <div className={styles.completeSection}>
                                <div className={styles.sectionHeader}>
                                    <h4>
                                        <i className="pi pi-check-circle" />
                                        Review & Complete
                                    </h4>
                                    <p>Confirm all details before submitting</p>
                                </div>

                                {hasPartialScan && (
                                    <div className={styles.partialScanWarning}>
                                        <i className="pi pi-exclamation-triangle" />
                                        <div className={styles.warningContent}>
                                            <strong>Partial Delivery Detected</strong>
                                            <p>
                                                Only {scannedPackages.length} of {totalPackages} packages scanned. You cannot complete full delivery. Use "Mark Remaining as Incomplete" instead.
                                            </p>
                                        </div>
                                    </div>
                                )}

                                <div className={styles.reviewGrid}>
                                    <div className={styles.reviewItem}>
                                        <h5>
                                            <i className="pi pi-qrcode" />
                                            Scanned Packages
                                        </h5>
                                        <p>
                                            {scannedPackages.length} / {totalPackages} packages
                                        </p>
                                        {hasPartialScan && <span className={styles.partialBadge}>Partial</span>}
                                    </div>
                                    <div className={styles.reviewItem}>
                                        <h5>
                                            <i className="pi pi-camera" />
                                            Delivery Photo
                                        </h5>
                                        <img src={photoDataUrl} alt="Preview" className={styles.reviewThumb} />
                                    </div>
                                    <div className={styles.reviewItem}>
                                        <h5>
                                            <i className="pi pi-pencil" />
                                            Signature
                                        </h5>
                                        <img src={signatureDataUrl} alt="Preview" className={styles.reviewThumb} />
                                    </div>
                                </div>

                                <Divider />

                                <div className={styles.finalActions}>
                                    <Button
                                        label={navigator.onLine ? "Complete Delivery" : "Complete (Save Offline)"}
                                        icon={isSubmitting ? "pi pi-spin pi-spinner" : navigator.onLine ? "pi pi-check" : "pi pi-save"}
                                        onClick={handleSubmitDelivery}
                                        disabled={isSubmitting || hasPartialScan}
                                        className={styles.completeButton}
                                        tooltip={hasPartialScan ? "Cannot complete - partial scan detected" : ""}
                                    />
                                    {hasPartialScan && <Button label="Mark Remaining as Incomplete" icon="pi pi-exclamation-triangle" onClick={openIncompleteDialog} severity="danger" className={styles.incompleteButton} />}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </Dialog>

            {/* SKIP STOP DIALOG */}
            <Dialog visible={showSkipDialog} onHide={() => setShowSkipDialog(false)} header="Skip This Stop" className={styles.skipDialog} draggable={false} blockScroll>
                <div className={styles.skipContent}>
                    <p className={styles.infoText}>
                        <i className="pi pi-info-circle" />
                        This stop will be marked as skipped and can be attempted later in the route.
                    </p>

                    {!navigator.onLine && (
                        <div className={styles.offlineWarning}>
                            <i className="pi pi-wifi" style={{ textDecoration: "line-through" }} />
                            <span>Offline - Status will be saved locally and synced when online</span>
                        </div>
                    )}

                    <div className={styles.reasonOptions}>
                        {["Skipped to next delivery", "Will return later", "Customer not ready", "Delivery time not convenient", "Address needs verification", "Traffic/Route optimization", "Other"].map((reason) => (
                            <Button key={reason} label={reason} outlined={skipReason !== reason} severity={skipReason === reason ? "info" : "secondary"} onClick={() => setSkipReason(reason)} className={styles.reasonButton} />
                        ))}
                    </div>

                    {skipReason === "Other" && <InputTextarea value={customSkipReason} onChange={(e) => setCustomSkipReason(e.target.value)} placeholder="Enter reason for skipping..." rows={3} className={styles.customReason} autoFocus />}

                    <div className={styles.skipActions}>
                        <Button
                            label="Cancel"
                            icon="pi pi-times"
                            onClick={() => {
                                setShowSkipDialog(false);
                                setSkipReason("");
                                setCustomSkipReason("");
                            }}
                            outlined
                        />
                        <Button
                            label={navigator.onLine ? "Confirm Skip" : "Save Offline"}
                            icon={isSkipping ? "pi pi-spin pi-spinner" : navigator.onLine ? "pi pi-step-forward" : "pi pi-save"}
                            onClick={handleSkipStop}
                            disabled={!skipReason || (skipReason === "Other" && !customSkipReason.trim()) || isSkipping}
                            severity="info"
                        />
                    </div>
                </div>
            </Dialog>

            {/* INCOMPLETE REASON DIALOG */}
            <Dialog visible={showIncompleteDialog} onHide={() => setShowIncompleteDialog(false)} header="Mark Stop as Incomplete" className={styles.incompleteDialog} draggable={false} blockScroll>
                <div className={styles.incompleteContent}>
                    <p className={styles.warningText}>
                        <i className="pi pi-exclamation-triangle" />
                        This will mark the stop as incomplete and require handler attention.
                    </p>

                    {!navigator.onLine && (
                        <div className={styles.offlineWarning}>
                            <i className="pi pi-wifi" style={{ textDecoration: "line-through" }} />
                            <span>Offline - Status will be saved locally and synced when online</span>
                        </div>
                    )}

                    {hasPartialScan && (
                        <div className={styles.partialScanInfo}>
                            <i className="pi pi-info-circle" />
                            <div>
                                <strong>Partial Delivery</strong>
                                <p>
                                    {scannedPackages.length} of {totalPackages} packages will be marked as delivered with proof.
                                </p>
                            </div>
                        </div>
                    )}

                    <div className={styles.reasonOptions}>
                        {["Customer not available", "Wrong address", "Access denied", "Delivery refused", "Business closed", "Weather conditions", "Vehicle breakdown", "Package damaged", "Incorrect package count", "Other"].map((reason) => (
                            <Button key={reason} label={reason} outlined={incompleteReason !== reason} severity={incompleteReason === reason ? "danger" : "secondary"} onClick={() => setIncompleteReason(reason)} className={styles.reasonButton} />
                        ))}
                    </div>

                    {incompleteReason === "Other" && <InputTextarea value={customIncompleteReason} onChange={(e) => setCustomIncompleteReason(e.target.value)} placeholder="Enter custom reason..." rows={3} className={styles.customReason} autoFocus />}

                    <div className={styles.incompleteActions}>
                        <Button
                            label="Cancel"
                            icon="pi pi-times"
                            onClick={() => {
                                setShowIncompleteDialog(false);
                                setIncompleteReason("");
                                setCustomIncompleteReason("");
                            }}
                            outlined
                        />
                        <Button
                            label={navigator.onLine ? "Confirm Incomplete" : "Save Offline"}
                            icon={isMarkingIncomplete ? "pi pi-spin pi-spinner" : navigator.onLine ? "pi pi-check" : "pi pi-save"}
                            onClick={handleMarkIncomplete}
                            disabled={!incompleteReason || (incompleteReason === "Other" && !customIncompleteReason.trim()) || isMarkingIncomplete}
                            severity="danger"
                        />
                    </div>
                </div>
            </Dialog>
        </>
    );
};

export default DeliveryStopDialog;
